/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import debounce from 'lodash/debounce';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IRefineItemProps, IRefineItemState } from './refine-item';
import CustomRangeSlider from './custom-range-slider';

type InputType = 'Min' | 'Max';

export type RangeRefineItemType = 'slider' | 'input';

/**
 * Range refine item properties.
 */
export interface IRangeRefineItemProps extends IRefineItemProps {
    rangeType: RangeRefineItemType;
    minValueSliderThumbAriaLabel?: string;
    maxValueSliderThumbAriaLabel?: string;
    sliderKey?: string;
    /**
     * Callback to notify parent when validation error state changes.
     * @param hasError - True if there is a validation error, false otherwise.
     */
    onValidationErrorChange?: (hasError: boolean) => void;
}

/**
 * Range refine item state.
 */
export interface IRangeRefineItemState extends IRefineItemState {
    validationErrorMin: string | undefined;
    validationErrorMax: string | undefined;
    selectedMin: string | undefined;
    selectedMax: string | undefined;
    touchedMin: boolean;
    touchedMax: boolean;
    sliderId: string;
    minValue: string | undefined;
    maxValue: string | undefined;
    // Preserved initial bounds - these don't change when filters are applied
    initialMinValue: string | undefined;
    initialMaxValue: string | undefined;
}

/**
 * RangeRefineItem component (controlled by RefineSubmenu).
 */
@observer
export default class RangeRefineItem extends React.Component<IRangeRefineItemProps, IRangeRefineItemState> {
    private readonly _formattedPriceReverseLookup: Map<string, string> = new Map();

    private readonly minInput: React.RefObject<HTMLInputElement>;

    private readonly maxInput: React.RefObject<HTMLInputElement>;

    @computed get currencyCode(): string {
        return this.props.context.request.channel?.Currency || this.props.productRefinerValue.UnitText || '';
    }

    public constructor(props: IRangeRefineItemProps) {
        super(props);
        this._onSliderChange = this._onSliderChange.bind(this);
        this._onSliderChangeEnd = this._onSliderChangeEnd.bind(this);
        this._onSliderChangeDebounced = debounce(this._onSliderChangeDebounced.bind(this), 300);
        this._changeMin = this._changeMin.bind(this);
        this._changeMax = this._changeMax.bind(this);
        this._finishChangeMin = this._finishChangeMin.bind(this);
        this._finishChangeMax = this._finishChangeMax.bind(this);
        this._handleInputFocus = this._handleInputFocus.bind(this);
        this._onInputChangeDebounced = debounce(this._onInputChangeDebounced.bind(this), 500);

        this.minInput = React.createRef<HTMLInputElement>();
        this.maxInput = React.createRef<HTMLInputElement>();

        const { selectedRefinementCriterion } = this.props;
        const minValue = this.props.productRefinerValue?.LeftValueBoundString;
        const maxValue = this.props.productRefinerValue?.RightValueBoundString;
        // Store initial bounds - these will be preserved when filters change (rounded to whole numbers)
        const initialMinValue = minValue ? String(Math.floor(Number(minValue))) : minValue;
        const initialMaxValue = maxValue ? String(Math.ceil(Number(maxValue))) : maxValue;
        // Initialize to selected values if available, otherwise use the available min/max bounds (rounded)
        const rawInitialMin = selectedRefinementCriterion?.LeftValueBoundString ?? minValue ?? '0';
        const rawInitialMax = selectedRefinementCriterion?.RightValueBoundString ?? maxValue;
        const initialMin = String(Math.floor(Number(rawInitialMin)));
        const initialMax = rawInitialMax ? String(Math.ceil(Number(rawInitialMax))) : rawInitialMax;
        const sliderId = `slider_${String(this.props.parentProductRefinerHierarchy.RecordId)}_${String(
            this.props.productRefinerValue?.RefinerRecordId
        )}`;

        this.state = {
            isChecked: false,
            validationErrorMin: undefined,
            validationErrorMax: undefined,
            selectedMin: initialMin,
            selectedMax: initialMax,
            touchedMin: false,
            touchedMax: false,
            sliderId,
            minValue,
            maxValue,
            initialMinValue,
            initialMaxValue
        };
    }

    public shouldComponentUpdate(nextProps: IRangeRefineItemProps, nextState: IRangeRefineItemState): boolean {
        if (this.state === nextState && this.props === nextProps) {
            return false;
        }
        return true;
    }

    public componentDidUpdate(previousProps: IRangeRefineItemProps, previousState: IRangeRefineItemState): void {
        if (previousProps !== this.props) {
            this.updateSliderProps();
        }

        // Notify parent when validation error state changes
        const prevHasError = !!(previousState.validationErrorMin || previousState.validationErrorMax);
        const currentHasError = !!(this.state.validationErrorMin || this.state.validationErrorMax);
        if (prevHasError !== currentHasError && this.props.onValidationErrorChange) {
            this.props.onValidationErrorChange(currentHasError);
        }
    }

    public render(): JSX.Element | undefined {
        const { productRefinerValue, parentProductRefinerHierarchy, refineItemCommonProps } = this.props;

        if (!productRefinerValue || !parentProductRefinerHierarchy) {
            refineItemCommonProps.telemetry.error(
                'Cannot render refine value range without productRefinerValue and parentProductRefinerHierarchy'
            );
            return undefined;
        }

        if (this.props.rangeType === 'input') {
            return this._renderInputFields();
        }

        return this._renderSlider();
    }

    /**
     * Function to update slider props.
     */
    private updateSliderProps(): void {
        const sliderId = `slider_${String(this.props.parentProductRefinerHierarchy.RecordId)}_${String(
            this.props.productRefinerValue.RefinerRecordId
        )}`;
        // Use preserved initial bounds instead of current props bounds
        const minValue = this.state.initialMinValue;
        const maxValue = this.state.initialMaxValue;
        const selectedMin = this.props.selectedRefinementCriterion?.LeftValueBoundString ?? minValue ?? '0';
        const selectedMax = this.props.selectedRefinementCriterion?.RightValueBoundString ?? maxValue;

        // Update state but preserve user's typed value when they're actively editing
        this.setState(prevState => ({
            sliderId,
            minValue,
            maxValue,
            // Only update selectedMin/Max if user is NOT currently editing that field
            selectedMin: prevState.touchedMin ? prevState.selectedMin : selectedMin,
            selectedMax: prevState.touchedMax ? prevState.selectedMax : selectedMax
        }));
    }

    private _renderInputFields(): JSX.Element | undefined {
        const { isDisabled, parentProductRefinerHierarchy, refineItemCommonProps } = this.props;
        const { selectedMin, selectedMax, touchedMin, touchedMax, validationErrorMin, validationErrorMax } = this.state;

        const rangeAriaLabel = (refineItemCommonProps.rangeNameFormat || '{0}').replace('{0}', parentProductRefinerHierarchy.KeyName || '');
        const formAttrs = {
            'aria-label': rangeAriaLabel,
            'aria-disabled': isDisabled
        };

        // To enable price formatting of selected fields, this is the approach:
        // initial value: min=0 formatted as price, max=undefined
        // onFocus/onChange: convert to number (unformat) and mark as touched to indicate value is being edited
        // onBlur: validate and format entered value as price
        const minInputClassName = `ms-refine-submenu__input-range refine-submenu__input-range-min ${
            validationErrorMin ? 'refine-submenu__input-range--error' : ''
        }`;
        const maxInputClassName = `ms-refine-submenu__input-range refine-submenu__input-range-max ${
            validationErrorMax ? 'refine-submenu__input-range--error' : ''
        }`;
        const minLabelClassName = 'ms-refine-submenu__input-range-label refine-submenu__input-range-label-min';
        const maxLabelClassName = 'ms-refine-submenu__input-range-label refine-submenu__input-range-label-max';
        const formattedSelectedMin = this._getFormattedSelectedValue(selectedMin, touchedMin, validationErrorMin);
        const formattedSelectedMax = this._getFormattedSelectedValue(selectedMax, touchedMax, validationErrorMax);
        return (
            <form className='ms-refine-submenu__input-range-refiner' {...formAttrs}>
                <label className={minLabelClassName}>
                    {refineItemCommonProps.minLabel}
                    <input
                        className={minInputClassName}
                        onChange={this._changeMin}
                        onFocus={() => this._handleInputFocus('Min')}
                        onBlur={this._finishChangeMin}
                        value={formattedSelectedMin}
                        ref={this.minInput}
                    />
                </label>
                <label className={maxLabelClassName}>
                    {refineItemCommonProps.maxLabel}
                    <input
                        className={maxInputClassName}
                        placeholder={formattedSelectedMax ? undefined : refineItemCommonProps.placeholderTextMax}
                        onChange={this._changeMax}
                        onFocus={() => this._handleInputFocus('Max')}
                        onBlur={this._finishChangeMax}
                        value={formattedSelectedMax}
                        ref={this.maxInput}
                    />
                </label>
                {validationErrorMin && (
                    <span className='ms-refine-submenu__input-range-error-text refine-submenu__input-range-min-error-text'>
                        {validationErrorMin}
                    </span>
                )}
                {validationErrorMax && validationErrorMin !== validationErrorMax && (
                    <span className='ms-refine-submenu__input-range-error-text refine-submenu__input-range-max-error-text'>
                        {validationErrorMax}
                    </span>
                )}
            </form>
        );
    }

    private _renderSlider(): JSX.Element | undefined {
        const { isDisabled, refineItemCommonProps } = this.props;
        const { selectedMin, selectedMax, touchedMin, touchedMax, validationErrorMin, validationErrorMax } = this.state;

        const minInputClassName = `ms-refine-submenu__input-range refine-submenu__input-range-min ${
            validationErrorMin ? 'refine-submenu__input-range--error' : ''
        }`;
        const maxInputClassName = `ms-refine-submenu__input-range refine-submenu__input-range-max ${
            validationErrorMax ? 'refine-submenu__input-range--error' : ''
        }`;
        const formattedSelectedMin = this._getFormattedSelectedValue(selectedMin, touchedMin, validationErrorMin);
        const formattedSelectedMax = this._getFormattedSelectedValue(selectedMax, touchedMax, validationErrorMax);

        // Slider values - use whole numbers
        const sliderMin = Math.floor(Number(this.state.minValue) || 0);
        const sliderMax = Math.ceil(Number(this.state.maxValue) || 100);
        const currentMin = Math.floor(Number(selectedMin) || sliderMin);
        const currentMax = Math.ceil(Number(selectedMax) || sliderMax);

        // Formatted labels
        const minPriceLabel = this._formatPrice(this.state.minValue);
        const maxPriceLabel = this._formatPrice(this.state.maxValue);

        return (
            <div className='ms-refine-submenu__range-refiner-container'>
                <CustomRangeSlider
                    id={this.state.sliderId}
                    className='ms-refine-submenu__range-refiner'
                    min={sliderMin}
                    max={sliderMax}
                    step={1}
                    minValue={currentMin}
                    maxValue={currentMax}
                    minLabel={minPriceLabel}
                    maxLabel={maxPriceLabel}
                    disabled={isDisabled}
                    formatTooltip={(value: number) => this._formatPrice(String(value))}
                    onChange={this._onSliderChange}
                    onChangeEnd={this._onSliderChangeEnd}
                />
                <div className='ms-refine-submenu__slider-input-fields'>
                    <div className='ms-refine-submenu__slider-input-group'>
                        <input
                            className={minInputClassName}
                            onChange={this._changeMin}
                            onFocus={() => this._handleInputFocus('Min')}
                            onBlur={this._finishChangeMin}
                            value={formattedSelectedMin}
                            ref={this.minInput}
                            aria-label={refineItemCommonProps.minLabel}
                        />
                        <span className='ms-refine-submenu__slider-input-label'>{refineItemCommonProps.minLabel}</span>
                    </div>
                    <span className='ms-refine-submenu__slider-input-separator'>to</span>
                    <div className='ms-refine-submenu__slider-input-group'>
                        <input
                            className={maxInputClassName}
                            onChange={this._changeMax}
                            onFocus={() => this._handleInputFocus('Max')}
                            onBlur={this._finishChangeMax}
                            value={formattedSelectedMax}
                            ref={this.maxInput}
                            aria-label={refineItemCommonProps.maxLabel}
                        />
                        <span className='ms-refine-submenu__slider-input-label'>{refineItemCommonProps.maxLabel}</span>
                    </div>
                </div>
                {validationErrorMin && (
                    <span className='ms-refine-submenu__input-range-error-text refine-submenu__input-range-min-error-text'>
                        {validationErrorMin}
                    </span>
                )}
                {validationErrorMax && validationErrorMin !== validationErrorMax && (
                    <span className='ms-refine-submenu__input-range-error-text refine-submenu__input-range-max-error-text'>
                        {validationErrorMax}
                    </span>
                )}
            </div>
        );
    }

    private _changeMin(event: React.ChangeEvent<HTMLInputElement>): void {
        this._changeValue(event, 'Min');
    }

    private _changeMax(event: React.ChangeEvent<HTMLInputElement>): void {
        this._changeValue(event, 'Max');
    }

    private _changeValue(event: React.ChangeEvent<HTMLInputElement>, inputType: InputType): void {
        const selectedKey = `selected${inputType}`;
        const touchedKey = `touched${inputType}`;
        const inputValue = event.currentTarget.value;

        // Allow empty input, digits, decimal point, and currency symbols
        // Regex allows: optional currency symbol, digits, optional decimal with up to 2 digits
        // (decimals allowed during typing but will be rounded on blur)
        const priceRegex = /^[£$€]?\s*\d*\.?\d{0,2}$/;

        if (inputValue === '' || priceRegex.test(inputValue)) {
            const numericValue = this._getInputWithoutFormatting(inputValue);
            const newValue = Number(numericValue);

            // Get the other value for range validation
            const { refineItemCommonProps } = this.props;
            let minValue: number;
            let maxValue: number;

            if (inputType === 'Min') {
                minValue = isNaN(newValue) ? 0 : newValue;
                maxValue = Number(this.state.selectedMax) || 0;
            } else {
                minValue = Number(this.state.selectedMin) || 0;
                maxValue = isNaN(newValue) ? 0 : newValue;
            }

            // Check for range validation error (min > max) - only when both values are valid
            // This allows the user to type any value; clamping to bounds happens on blur
            const hasRangeError = !isNaN(minValue) && !isNaN(maxValue) && maxValue > 0 && minValue > maxValue;

            this.setState({
                [selectedKey]: numericValue,
                [touchedKey]: true,
                validationErrorMin: hasRangeError ? refineItemCommonProps.validationErrorRange : undefined,
                validationErrorMax: hasRangeError ? refineItemCommonProps.validationErrorRange : undefined
            });

            // Apply filter with debounce if range is valid and both values are numbers
            if (!hasRangeError && !isNaN(minValue) && !isNaN(maxValue) && maxValue > 0) {
                this._onInputChangeDebounced(minValue, maxValue);
            }
        }
    }

    /**
     * Debounced handler to apply the price filter while user is typing.
     * Only called when the range is valid (min <= max).
     * @param minValue - The minimum value.
     * @param maxValue - The maximum value.
     */
    private _onInputChangeDebounced(minValue: number, maxValue: number): void {
        const { onToggle, parentProductRefinerHierarchy, productRefinerValue } = this.props;

        if (productRefinerValue) {
            onToggle({
                parentProductRefinerHierarchy,
                productRefinerValue,
                isSelecting: true,
                rangeStart: minValue,
                rangeEnd: maxValue
            });
        }
    }

    private _handleInputFocus(inputType: InputType): void {
        const touchedKey = `touched${inputType}`;
        this.setState({
            [touchedKey]: true
        });
    }

    private _finishChangeMin(event: React.FocusEvent<HTMLInputElement>): boolean {
        const selectedMinValue = this._getInputWithoutFormatting(event.currentTarget.value);
        // Round down (floor) for minimum value
        const minInput = Math.floor(Number(selectedMinValue));
        this.setState({
            selectedMin: String(minInput),
            touchedMin: false
        });
        const {
            onToggle,
            parentProductRefinerHierarchy,
            productRefinerValue,
            refineItemCommonProps,
            selectedRefinementCriterion
        } = this.props;
        const max = (selectedRefinementCriterion && selectedRefinementCriterion.RightValueBoundString) || this.state.selectedMax;

        const maxNum = max ? Number(max) : undefined;

        if (isNaN(minInput)) {
            this.setState({ validationErrorMin: refineItemCommonProps.validationErrorNaN });
            this._focus('Min');
            return false;
        }

        // Validate min is not below the available minimum (use preserved initial bounds)
        const availableMin = Number(this.state.initialMinValue ?? productRefinerValue.LeftValueBoundString) || 0;
        const availableMax = Number(this.state.initialMaxValue ?? productRefinerValue.RightValueBoundString);

        if (minInput < availableMin) {
            this.setState({
                selectedMin: String(availableMin),
                validationErrorMin: undefined,
                validationErrorMax: undefined
            });
            // Use the available minimum instead
            if (this._validateRange(availableMin, maxNum)) {
                this._updateSliderThumbsFromInput(availableMin, maxNum);
                onToggle({
                    parentProductRefinerHierarchy,
                    productRefinerValue,
                    isSelecting: true,
                    rangeStart: availableMin,
                    rangeEnd: maxNum
                });
            }
            return true;
        }

        // Validate min is not above the available maximum - reset to available min
        if (!isNaN(availableMax) && minInput > availableMax) {
            this.setState({
                selectedMin: String(availableMin),
                validationErrorMin: undefined,
                validationErrorMax: undefined
            });
            // Use the available minimum instead
            if (this._validateRange(availableMin, maxNum)) {
                this._updateSliderThumbsFromInput(availableMin, maxNum);
                onToggle({
                    parentProductRefinerHierarchy,
                    productRefinerValue,
                    isSelecting: true,
                    rangeStart: availableMin,
                    rangeEnd: maxNum
                });
            }
            return true;
        }

        if (this._validateRange(minInput, maxNum)) {
            // Clear validation errors (both min and max since they were set together when invalid)
            this.setState({ validationErrorMin: undefined, validationErrorMax: undefined });
            this._updateSliderThumbsFromInput(minInput, maxNum);

            onToggle({
                parentProductRefinerHierarchy,
                productRefinerValue,
                isSelecting: true,
                rangeStart: minInput,
                rangeEnd: maxNum
            });
            return true;
        }
        this._focus('Min');

        return false;
    }

    private _finishChangeMax(event: React.FocusEvent<HTMLInputElement>): boolean {
        const selectedMaxValue = this._getInputWithoutFormatting(event.currentTarget.value);
        // Round up (ceil) for maximum value
        const maxInput = Math.ceil(Number(selectedMaxValue));
        this.setState({
            selectedMax: String(maxInput),
            touchedMax: false
        });
        const {
            onToggle,
            parentProductRefinerHierarchy,
            productRefinerValue,
            refineItemCommonProps,
            selectedRefinementCriterion
        } = this.props;
        const min = (selectedRefinementCriterion && selectedRefinementCriterion.LeftValueBoundString) || this.state.selectedMin || '0';

        const minNum = Number(min);

        if (isNaN(maxInput)) {
            this.setState({ validationErrorMax: refineItemCommonProps.validationErrorNaN });
            this._focus('Max');
            return false;
        }

        // Validate max is not above the available maximum (use preserved initial bounds)
        const availableMax = Number(this.state.initialMaxValue ?? productRefinerValue.RightValueBoundString);
        const availableMin = Number(this.state.initialMinValue ?? productRefinerValue.LeftValueBoundString) || 0;

        if (!isNaN(availableMax) && maxInput > availableMax) {
            this.setState({
                selectedMax: String(availableMax),
                validationErrorMin: undefined,
                validationErrorMax: undefined
            });
            // Use the available maximum instead
            if (this._validateRange(minNum, availableMax)) {
                this._updateSliderThumbsFromInput(minNum, availableMax);
                if (productRefinerValue) {
                    onToggle({
                        parentProductRefinerHierarchy,
                        productRefinerValue,
                        isSelecting: true,
                        rangeStart: minNum,
                        rangeEnd: availableMax
                    });
                }
            }
            return true;
        }

        // Validate max is not below the available minimum - reset to available max
        if (maxInput < availableMin) {
            this.setState({
                selectedMax: String(availableMax),
                validationErrorMin: undefined,
                validationErrorMax: undefined
            });
            // Use the available maximum instead
            if (this._validateRange(minNum, availableMax)) {
                this._updateSliderThumbsFromInput(minNum, availableMax);
                if (productRefinerValue) {
                    onToggle({
                        parentProductRefinerHierarchy,
                        productRefinerValue,
                        isSelecting: true,
                        rangeStart: minNum,
                        rangeEnd: availableMax
                    });
                }
            }
            return true;
        }

        if (this._validateRange(minNum, maxInput)) {
            // Clear validation errors (both min and max since they were set together when invalid)
            this.setState({ validationErrorMin: undefined, validationErrorMax: undefined });
            this._updateSliderThumbsFromInput(minNum, maxInput);

            if (productRefinerValue) {
                onToggle({
                    parentProductRefinerHierarchy,
                    productRefinerValue,
                    isSelecting: true,
                    rangeStart: minNum,
                    rangeEnd: maxInput
                });
            } else {
                this._focus('Max');
            }
            return true;
        }

        return false;
    }

    private _focus(inputType: InputType): void {
        const ref = inputType === 'Max' ? this.maxInput : this.minInput;

        setTimeout(() => {
            if (ref && ref.current) {
                ref.current.focus();
            }
        }, 50);
    }

    /**
     * Updates selected values when input field values change.
     * @param minValue - The new minimum value.
     * @param maxValue - The new maximum value.
     */
    private _updateSliderThumbsFromInput(minValue: number, maxValue: number | undefined): void {
        // Just update the selected values - the slider will re-render with the new values
        this.setState({
            selectedMin: String(minValue),
            selectedMax: String(maxValue ?? this.state.maxValue)
        });
    }

    private _getFormattedSelectedValue(
        selected: string | undefined,
        touched: boolean,
        _validationError: string | undefined
    ): string | undefined {
        // Only return raw value when user is actively editing (touched)
        // Validation errors should not affect currency formatting
        if (touched || selected === undefined) {
            return selected;
        }
        return this._formatPrice(selected);
    }

    private _validateRange(min: number, max: number | undefined): boolean {
        const { refineItemCommonProps } = this.props;
        if (max === undefined) {
            return true;
        }

        if (min > max) {
            this.setState({
                validationErrorMin: refineItemCommonProps.validationErrorRange,
                validationErrorMax: refineItemCommonProps.validationErrorRange
            });
            return false;
        }

        return true;
    }

    private _formatPrice(amount: string | undefined): string {
        // Round to whole number and format without decimals
        const wholeNumber = Math.round(Number(amount));
        const result = this.props.context.cultureFormatter.formatCurrency(wholeNumber, this.currencyCode);
        // Remove decimal places from formatted string (e.g., "£5.00" -> "£5")
        return result.replace(/\.00$/, '');
    }

    private _getInputWithoutFormatting(input: string): string {
        // First try to cast raw input to a number
        const inputAsNum = Number(input);
        if (!isNaN(inputAsNum)) {
            return input;
        }

        // Try to extract numeric value from currency-formatted string (e.g., "£5.99" -> "5.99")
        // Remove currency symbols, commas, and whitespace
        const numericString = input.replace(/[£$€,\s]/g, '');
        const extractedNum = Number(numericString);
        if (!isNaN(extractedNum)) {
            return numericString;
        }

        // Otherwise try a reverse lookup and fall back to the raw input if all else fails
        const reverseLookupResult = this._formattedPriceReverseLookup.get(input);
        return reverseLookupResult || input;
    }

    /**
     * Handler for slider onChange event - updates local state for smooth UI.
     * @param minValue - The minimum value from the slider.
     * @param maxValue - The maximum value from the slider.
     */
    private _onSliderChange(minValue: number, maxValue: number): void {
        // Update local state immediately for responsive UI
        this.setState({
            selectedMin: String(minValue),
            selectedMax: String(maxValue),
            validationErrorMin: undefined,
            validationErrorMax: undefined
        });

        // Debounce the actual filter update
        this._onSliderChangeDebounced(minValue, maxValue);
    }

    /**
     * Debounced handler to update the filter after slider movement settles.
     * @param minValue - The minimum value from the slider.
     * @param maxValue - The maximum value from the slider.
     */
    private _onSliderChangeDebounced(minValue: number, maxValue: number): void {
        const { onToggle, parentProductRefinerHierarchy, productRefinerValue } = this.props;

        if (productRefinerValue) {
            onToggle({
                parentProductRefinerHierarchy,
                productRefinerValue,
                isSelecting: true,
                rangeStart: minValue,
                rangeEnd: maxValue
            });
        }
    }

    /**
     * Handler for slider onChangeEnd event - applies the filter.
     * @param minValue - The minimum value from the slider.
     * @param maxValue - The maximum value from the slider.
     */
    private _onSliderChangeEnd(minValue: number, maxValue: number): void {
        const { onToggle, parentProductRefinerHierarchy, productRefinerValue } = this.props;

        // Update local state
        this.setState({
            selectedMin: String(minValue),
            selectedMax: String(maxValue),
            validationErrorMin: undefined,
            validationErrorMax: undefined
        });

        if (productRefinerValue) {
            onToggle({
                parentProductRefinerHierarchy,
                productRefinerValue,
                isSelecting: true,
                rangeStart: minValue,
                rangeEnd: maxValue
            });
        }
    }
}
